// #region imports

// --- Angular
import { Inject, Injectable } from '@angular/core';

// --- RxJs
import { BehaviorSubject, Observable, fromEvent } from 'rxjs';

// --- 3rd Party
import { TranslateService } from '@ngx-translate/core';
import { JL } from 'jsnlog';

// --- HS Code
import { ApplicationError, ToastModel } from '../../models';
import { AppToastService } from './app-toast.service';
import { Utils } from '../../shared';
import { OidcService } from './account/oidc.service';
import { environment } from 'src/environments/environment';

// #endregion

@Injectable({
  providedIn: 'root'
})
export class AppService {
  private _refCount: number = 0;

  private _lastError$: BehaviorSubject<ApplicationError | null>;
  get lastError$(): Observable<ApplicationError | null> {
    return this._lastError$;
  }

  private _isOffLine$: BehaviorSubject<boolean>;
  get isOffLine$(): Observable<boolean>{
    return this._isOffLine$;
  }

  private _isLoading$: BehaviorSubject<boolean>;
  get isLoading$(): Observable<boolean> {
    return this._isLoading$;
  }

  private _showReportModal$: BehaviorSubject<boolean>;
  get showReportModal$(): Observable<boolean> {
    return this._showReportModal$;
  }

  constructor(
    @Inject('JSNLOG') private _logger: JL.JSNLog,
    private _oidcService: OidcService,
    private translate: TranslateService,
    private toastService: AppToastService
  ) {
    this._lastError$ = new BehaviorSubject<ApplicationError | null>(null);
    this._isOffLine$ = new BehaviorSubject<boolean>(false);
    this._isLoading$ = new BehaviorSubject<boolean>(false);
    this._showReportModal$ = new BehaviorSubject<boolean>(false);

    fromEvent<Event>(window, 'offline').subscribe(() => {
      this._isOffLine$.next(true);
    });

    fromEvent<Event>(window, 'online').subscribe(() => {
      this._isOffLine$.next(false);
    });
  }

  setLastError(error: ApplicationError | null): void {
    const subs = this._oidcService.isMobileUser$.subscribe(isMobileUser => {
      this._logger().error({
        isMobileUser: isMobileUser,
        errorMessage: 'An error has occurred in the application.',
        error: JSON.stringify(error)
      });

      if (isMobileUser && error?.isFatal) {
        const errorQuery = `?errorCode=${error.errorCode}&errorMessage=${error.errorMessage}`;
        const redirectUri = `${window.location.origin}${environment.mobileCallbackUrl}${errorQuery}`;
        window.location.href = redirectUri;
      } else {
        if (error?.showToast) {
          this.toastService.show({
            header: this.translate.instant(error?.errorHeader ?? 'errorHeader'),
            body: this.translate.instant(error?.displayMessage ?? 'errorMessageGenericBackend'),
            class: 'bg-danger text-light',
            autoHide: true,
            delay: 3000,
            link: error?.redirectTo,
            error
          } as ToastModel);
        }
        this._lastError$.next(error);

        subs.unsubscribe();
      }
    });
  }

  setLoading(isLoading: boolean): void {
    if (isLoading) {
      this._refCount++;
    } else {
      if (this._refCount > 0) this._refCount--;
    }
    this._isLoading$.next(this._refCount > 0);
  }
  
  resetLoading(): void {
    this._refCount = 0;
    this._isLoading$.next(false);
  }

  setShowReportModal(showModal: boolean) {
    this._showReportModal$.next(showModal);
  }

  createGuid(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      // tslint:disable-next-line:no-bitwise
      const r = Math.random() * 16 | 0;
      // tslint:disable-next-line:no-bitwise
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }
}
